import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/common/Footer';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-2">Last updated: April 2026</p>
        <p className="text-sm text-muted-foreground mb-10">
          Please read these Terms of Service carefully before using CTX. By accessing or using the
          Service, you confirm that you have read, understood, and agree to be bound by these Terms.
        </p>

        <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. Acceptance of Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you
              ("User", "you") and Cerebral Trading Labs ("CTX", "we", "us", "our") governing your
              access to and use of the CTX platform, including all associated websites, mobile
              applications, features, content, and services (collectively, the "Service").
            </p>
            <p className="mt-3">
              By creating an account, clicking "I agree," or otherwise accessing or using the
              Service, you accept these Terms in full. If you do not agree to these Terms, you must
              immediately cease using the Service.
            </p>
            <p className="mt-3">
              These Terms apply in addition to our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>{' '}
              and{' '}
              <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link>,
              which are incorporated by reference.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Eligibility</h2>
            <p>You must meet all of the following conditions to use the Service:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>You are at least 18 years of age, or the age of majority in your jurisdiction, whichever is greater.</li>
              <li>You have the legal capacity to enter into a binding contract.</li>
              <li>You are not located in, or a national or resident of, any country subject to applicable trade sanctions or embargoes.</li>
              <li>You are not prohibited from using the Service under any applicable law.</li>
            </ul>
            <p className="mt-3">
              By using the Service, you represent and warrant that you satisfy all eligibility
              requirements above. We reserve the right to terminate access for any user who does not
              meet these requirements.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. Description of Service</h2>
            <p>
              CTX is an educational trading journal platform designed to help traders develop
              self-awareness, discipline, and behavioral insight. The Service includes, but is not
              limited to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Trade logging and record-keeping tools</li>
              <li>Performance analytics and statistical summaries</li>
              <li>Psychology and behavioral analysis (Cortex)</li>
              <li>AI-powered journaling assistance (Chronicle / Journal.AI)</li>
              <li>AI coaching sessions (Sage AI Coach, Insight Lab)</li>
              <li>Playbook and rule-management tools (Blueprint)</li>
            </ul>
            <p className="mt-3">
              We reserve the right to modify, suspend, or discontinue any part of the Service at
              any time, with or without notice. We will not be liable to you or any third party for
              any modification, suspension, or discontinuation.
            </p>
          </section>

          {/* 4 — prominent */}
          <section className="border border-border rounded-lg p-5 bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-foreground mb-3">
              4. Important Disclaimer: CTX Is Not Financial Advice
            </h2>
            <p className="font-medium text-foreground mb-3">
              CTX is an educational self-reflection tool. Nothing on this platform constitutes
              financial advice, investment advice, trading recommendations, or any form of
              regulated financial guidance.
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                All analytics, scores, insights, behavioral summaries, and AI-generated content
                are provided solely for educational and self-reflective purposes.
              </li>
              <li>
                CTX does not provide buy or sell signals, trading recommendations, or personalised
                investment strategies of any kind.
              </li>
              <li>
                Past performance reflected in your journal data does not guarantee, predict, or
                imply future trading results.
              </li>
              <li>
                Trading financial instruments including forex, futures, stocks, and derivatives
                involves substantial risk of loss. You may lose more than your initial capital.
              </li>
              <li>
                You are solely and exclusively responsible for all trading decisions you make. CTX
                accepts no responsibility whatsoever for any trading losses, missed opportunities,
                or financial outcomes arising from your use of the Service.
              </li>
              <li>
                If you require financial advice, please consult a licensed financial adviser or
                appropriately regulated professional in your jurisdiction.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. AI Features Disclaimer</h2>
            <p>
              Certain features of the Service (including Sage AI Coach, Insight Lab, and
              Journal.AI) use large-language-model AI technology to analyse patterns in your trading
              data and generate responses. By using these features, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                AI-generated content is produced by automated systems and may be inaccurate,
                incomplete, outdated, or inappropriate for your circumstances.
              </li>
              <li>
                AI responses are not a substitute for professional financial advice, licensed
                psychological counselling, or any other form of regulated professional service.
              </li>
              <li>
                We do not guarantee the accuracy, reliability, or suitability of any AI-generated
                content.
              </li>
              <li>
                Content entered into AI features (trade notes, journal entries, chat messages) may
                be processed by a third-party AI language model provider to generate responses.
                Please refer to our Privacy Policy for details.
              </li>
              <li>
                If trading losses are causing you significant emotional distress, please seek
                support from a qualified mental health professional. AI coaching is not therapy.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. User Accounts</h2>
            <p className="mb-2">
              To access the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide accurate, current, and complete information during registration and keep it up to date.</li>
              <li>
                Maintain the confidentiality of your account credentials, including your password.
                You are solely responsible for all activity occurring under your account.
              </li>
              <li>Notify us immediately at the contact details below if you suspect any unauthorised use of your account.</li>
              <li>
                Not create multiple accounts to circumvent tier limits, abuse free trials, or
                exploit any promotional offer.
              </li>
              <li>Not transfer, sell, or assign your account to any third party.</li>
            </ul>
            <p className="mt-3">
              We are not liable for any loss or damage arising from your failure to maintain the
              security of your account credentials.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              7. Subscriptions, Payments & Refunds
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                CTX offers a free tier and paid subscription tiers (Basic, Pro, Elite). Paid
                subscriptions are billed on a recurring monthly or annual basis.
              </li>
              <li>
                All prices are displayed in USD and are exclusive of any applicable taxes. You are
                responsible for any taxes, duties, or levies imposed by your local jurisdiction.
              </li>
              <li>
                Payments are processed by a third-party payment processor. By subscribing, you
                authorise us to charge your payment method on a recurring basis at the applicable
                rate until you cancel. We do not store your full payment card details.
              </li>
              <li>
                You may cancel your subscription at any time through your account settings or by
                contacting us. Cancellation takes effect at the end of the current billing period.
                You will retain access to paid features until the end of the period for which you
                have paid.
              </li>
              <li>
                <strong className="text-foreground">Refunds:</strong> Subscription fees are
                generally non-refundable. We may, at our sole discretion, issue a refund or credit
                in exceptional circumstances. To request a refund, contact us within 7 days of a
                charge.
              </li>
              <li>
                We reserve the right to change subscription pricing at any time. We will give you
                at least 30 days' notice of any price increase before it takes effect for your
                account.
              </li>
              <li>
                Downgrading your subscription may result in loss of access to features and data
                associated with your previous tier. We are not responsible for any data loss
                resulting from a voluntary downgrade.
              </li>
            </ul>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Acceptable Use Policy</h2>
            <p className="mb-2">You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Violate any applicable law, regulation, or third-party rights.</li>
              <li>Engage in fraud, misrepresentation, or deceptive practices.</li>
              <li>Upload, transmit, or store any content that is illegal, harmful, defamatory, or infringes any intellectual property right.</li>
              <li>Attempt to gain unauthorised access to the Service, its infrastructure, or other users' accounts.</li>
              <li>Reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service.</li>
              <li>Use automated bots, scrapers, or scripts to access, collect data from, or interact with the Service without express written permission.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service or its underlying infrastructure.</li>
              <li>Share, resell, or sublicense your account or access to the Service to any third party.</li>
              <li>Use the AI features to generate content that promotes harmful trading behaviour or that you intend to redistribute as financial advice.</li>
            </ul>
            <p className="mt-3">
              We reserve the right to suspend or terminate access immediately for any violation of
              this policy, without prior notice or refund.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Intellectual Property</h2>
            <p>
              All content, software, design, trademarks, logos, and materials comprising the
              Service ("CTX Content") are owned by or licensed to Cerebral Trading Labs and are
              protected by applicable intellectual property laws. You are granted a limited,
              non-exclusive, non-transferable, revocable licence to access and use the Service
              solely for your own personal, non-commercial purposes in accordance with these Terms.
            </p>
            <p className="mt-3">
              You may not copy, modify, distribute, sell, sublicense, or create derivative works
              based on CTX Content without our express written permission.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">10. User Content</h2>
            <p>
              You retain ownership of all trading data, journal entries, notes, and other content
              you upload or create within the Service ("User Content"). By submitting User Content,
              you grant us a limited, royalty-free licence to store, process, and display your User
              Content solely for the purpose of operating and providing the Service to you.
            </p>
            <p className="mt-3">
              We do not claim ownership of your User Content, use it for marketing purposes, or
              share it with third parties except as described in our Privacy Policy.
            </p>
            <p className="mt-3">
              You are solely responsible for the accuracy of the trading data you enter. CTX does
              not verify trade records and accepts no liability for analyses or insights generated
              from inaccurate data you have provided.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT THE
              SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE FROM VIRUSES OR OTHER
              HARMFUL COMPONENTS.
            </p>
            <p className="mt-3">
              We do not warrant that: (a) the Service will meet your requirements; (b) any results
              obtained from use of the Service will be accurate or reliable; (c) AI-generated
              content will be accurate, complete, or appropriate for your purposes.
            </p>
            <p className="mt-3">
              Some jurisdictions do not allow the exclusion of implied warranties. To the extent
              such exclusions are not permitted, they shall apply to the maximum extent permitted
              by applicable law.
            </p>
          </section>

          {/* 12 */}
          <section className="border border-border rounded-lg p-5 bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-foreground mb-3">12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CEREBRAL TRADING LABS, ITS
              DIRECTORS, EMPLOYEES, CONTRACTORS, AGENTS, AFFILIATES, AND LICENSORS SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
              DAMAGES, OR ANY LOSS OF PROFITS, TRADING LOSSES, REVENUE, DATA, GOODWILL, OR OTHER
              INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Your access to or use of (or inability to access or use) the Service;</li>
              <li>Any trading decisions made in reliance on the Service or AI-generated content;</li>
              <li>Any unauthorised access to or alteration of your data;</li>
              <li>Any interruption, suspension, or discontinuation of the Service;</li>
              <li>Any errors, inaccuracies, or omissions in AI-generated content or analytics.</li>
            </ul>
            <p className="mt-3">
              IN NO EVENT SHALL OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF
              OR RELATING TO THESE TERMS OR THE SERVICE EXCEED THE GREATER OF (A) THE TOTAL
              AMOUNTS PAID BY YOU TO CTX IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE
              CLAIM, OR (B) USD $50.
            </p>
            <p className="mt-3">
              The limitations above apply regardless of the legal theory (contract, tort,
              negligence, strict liability, or otherwise) and even if CTX has been advised of the
              possibility of such damages.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Cerebral Trading Labs and its
              directors, employees, agents, and licensors from and against any claims, liabilities,
              damages, losses, and expenses (including reasonable legal fees) arising out of or
              relating to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Your violation of these Terms;</li>
              <li>Your User Content;</li>
              <li>Your use of the Service in a manner not expressly authorised by these Terms;</li>
              <li>Any trading activity, investment decision, or financial outcome connected to your use of the Service.</li>
            </ul>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">14. Third-Party Services</h2>
            <p>
              The Service integrates with or relies upon third-party services including payment
              processors, cloud infrastructure, AI language model providers, and analytics tools.
              These third parties have their own terms of service and privacy policies, which govern
              their respective services.
            </p>
            <p className="mt-3">
              The Service may contain links to third-party websites or resources. Such links do not
              constitute our endorsement of those resources. We are not responsible for the
              content, availability, or practices of any third-party site or service, and you
              access them at your own risk.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">15. Termination</h2>
            <p>
              We may suspend or terminate your account and access to the Service at our sole
              discretion, at any time, with or without cause, and with or without notice. Grounds
              for termination include but are not limited to: violation of these Terms, non-payment,
              conduct harmful to other users, or any behaviour that we reasonably believe exposes
              us to legal liability.
            </p>
            <p className="mt-3">
              You may terminate your account at any time by contacting us or using the account
              deletion option in your settings. Upon termination, your licence to use the Service
              ends immediately.
            </p>
            <p className="mt-3">
              You may request an export of your data before account deletion. Following account
              deletion, we will retain your data only as required by applicable law or for
              legitimate business purposes, and will delete it in accordance with our Privacy
              Policy.
            </p>
            <p className="mt-3">
              Sections 4, 9, 11, 12, 13, and 16 survive termination.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              16. Governing Law & Jurisdiction
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of
              the Province of Ontario, Canada, without regard to its conflict of law provisions.
              You agree to submit to the exclusive jurisdiction of the courts located in Ontario,
              Canada for any disputes arising under or in connection with these
              Terms.
            </p>
            <p className="mt-3">
              Nothing in this section limits our right to seek injunctive or equitable relief in
              any court of competent jurisdiction to protect our intellectual property or
              confidential information.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">17. Dispute Resolution</h2>
            <p>
              We encourage you to contact us first if you have a concern or dispute. Most issues
              can be resolved quickly and informally. You agree to give us a reasonable opportunity
              to address any complaint before initiating formal legal proceedings.
            </p>
            <p className="mt-3">
              For users in jurisdictions where mandatory arbitration clauses are enforceable, we
              may require binding arbitration on an individual basis rather than class-action
              litigation. The specific arbitration terms will be provided upon request and as
              required by applicable law.
            </p>
          </section>

          {/* 18 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">18. General Provisions</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-foreground">Entire Agreement:</strong> These Terms,
                together with our Privacy Policy and Cookie Policy, constitute the entire agreement
                between you and CTX regarding the Service and supersede all prior agreements.
              </li>
              <li>
                <strong className="text-foreground">Severability:</strong> If any provision of
                these Terms is found to be unenforceable or invalid, that provision shall be
                limited or eliminated to the minimum extent necessary so that the remaining
                provisions remain in full force.
              </li>
              <li>
                <strong className="text-foreground">No Waiver:</strong> Our failure to enforce
                any right or provision of these Terms shall not constitute a waiver of that right
                or provision.
              </li>
              <li>
                <strong className="text-foreground">Assignment:</strong> You may not assign or
                transfer these Terms or any of your rights under them without our prior written
                consent. We may assign our rights and obligations without restriction.
              </li>
              <li>
                <strong className="text-foreground">Force Majeure:</strong> We shall not be liable
                for any delay or failure to perform resulting from causes outside our reasonable
                control, including natural disasters, acts of government, infrastructure failures,
                or internet disruptions.
              </li>
            </ul>
          </section>

          {/* 19 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">19. Changes to These Terms</h2>
            <p>
              We reserve the right to update or modify these Terms at any time. When we make
              material changes, we will notify you by email or by posting a prominent notice within
              the Service at least 14 days before the changes take effect. Your continued use of
              the Service after the effective date constitutes your acceptance of the revised Terms.
            </p>
            <p className="mt-3">
              If you do not agree to the updated Terms, you must stop using the Service and may
              request account deletion.
            </p>
          </section>

          {/* 20 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">20. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2">
              <strong className="text-foreground">Cerebral Trading Labs</strong><br />
              Email: support@ctxtraders.com<br />
              Ontario, Canada
            </p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-border text-xs text-muted-foreground">
          <p>
            These Terms of Service are provided for informational purposes and are intended to be
            reviewed by qualified legal counsel before being relied upon as final. They are not
            legal advice.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
